/**
 * Google sign-in for the admin write path.
 *
 * The admin signs in as a Google account that already holds write access to
 * the content buckets. Google Identity Services hands back a short-lived OAuth
 * access token scoped to Cloud Storage; no service-account key exists, ships,
 * or gets pasted anywhere.
 *
 * The client ID is public by design (it is sent to Google on every sign-in).
 * The token lives in memory and in sessionStorage for this tab only, so a
 * refresh keeps the session and closing the tab ends it.
 */

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GIS_SRC = "https://accounts.google.com/gsi/client";
const USERINFO_URL = "https://www.googleapis.com/oauth2/v3/userinfo";
const SCOPE = "https://www.googleapis.com/auth/devstorage.read_write";
const TOKEN_KEY = "pp-admin-google-token";
// Treat a token as expired a minute early so a request never races the edge.
const EARLY_MS = 60 * 1000;

/** @typedef {{ value: string, expires: number, email: string }} Token */
/** @typedef {{ fetch?: (url: string, init?: object) => Promise<any>, now?: () => number }} AuthDeps */

/** @type {Token | null} */
let _token = null;
/** @type {Promise<void> | null} */
let _scriptPromise = null;
/** @type {Promise<Token> | null} */
let _pending = null;

/** @returns {any} the GIS oauth2 namespace once the script has loaded. */
const gisOauth = () => /** @type {any} */ (window).google?.accounts?.oauth2;

/** True when a Google OAuth client ID is configured for this build. */
export function googleConfigured() {
	return Boolean(CLIENT_ID);
}

const isFresh = (t, now) => Boolean(t && t.value && t.expires > now() + EARLY_MS);

function readStored(now) {
	try {
		const t = JSON.parse(sessionStorage.getItem(TOKEN_KEY) || "null");
		return isFresh(t, now) ? t : null;
	} catch {
		return null;
	}
}

function store(t) {
	_token = t;
	try {
		if (t) sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t));
		else sessionStorage.removeItem(TOKEN_KEY);
	} catch {}
}

/**
 * The current token if it is still valid, without prompting.
 * @param {AuthDeps} [deps]
 * @returns {Token | null}
 */
export function currentToken({ now = Date.now } = {}) {
	if (!_token) _token = readStored(now);
	return isFresh(_token, now) ? _token : null;
}

function loadScript() {
	if (gisOauth()) return Promise.resolve();
	if (_scriptPromise) return _scriptPromise;
	_scriptPromise = new Promise((resolve, reject) => {
		const s = document.createElement("script");
		s.src = GIS_SRC;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => {
			_scriptPromise = null;
			reject(
				new Error("Could not load Google sign-in. Check your connection and try again."),
			);
		};
		document.head.appendChild(s);
	});
	return _scriptPromise;
}

async function fetchEmail(accessToken, fetchImpl) {
	try {
		const res = await fetchImpl(USERINFO_URL, {
			headers: { Authorization: `Bearer ${accessToken}` },
		});
		if (!res.ok) return "";
		const data = await res.json();
		return data.email || "";
	} catch {
		return "";
	}
}

function signInError(resp) {
	return new Error(
		resp.error === "access_denied"
			? "Google sign-in was cancelled."
			: `Google sign-in failed: ${resp.error_description || resp.error}.`,
	);
}

function popupError(err) {
	return new Error(
		err?.type === "popup_closed"
			? "The Google window was closed before signing in."
			: "Google sign-in could not open. Allow pop-ups for this site and try again.",
	);
}

/** Open a GIS token client and resolve with its successful token response. */
function askGoogle(interactive) {
	return new Promise((resolve, reject) => {
		const client = gisOauth().initTokenClient({
			client_id: CLIENT_ID,
			scope: SCOPE,
			callback: (resp) => (resp.error ? reject(signInError(resp)) : resolve(resp)),
			error_callback: (err) => reject(popupError(err)),
		});
		client.requestAccessToken({ prompt: interactive ? "select_account" : "" });
	});
}

// The email is read for every new token: a silent refresh may land on a
// different Google account, and the admin check must see the account in use.
async function signIn(interactive, { fetch: fetchImpl = fetch, now = Date.now }) {
	await loadScript();
	const resp = await askGoogle(interactive);
	const t = {
		value: resp.access_token,
		expires: now() + Number(resp.expires_in || 3600) * 1000,
		email: await fetchEmail(resp.access_token, fetchImpl),
	};
	store(t);
	return t;
}

/**
 * Get a Storage access token, prompting only when needed.
 * `interactive: true` shows Google's account chooser (must run from a click);
 * otherwise a previously consented account is reused silently. Concurrent
 * callers share one sign-in.
 * @param {{ interactive?: boolean }} [options]
 * @param {AuthDeps} [deps]
 * @returns {Promise<Token>} rejects with a user-readable message when sign-in fails.
 */
export async function requestToken({ interactive = false } = {}, deps = {}) {
	const cached = currentToken(deps);
	if (cached) return cached;
	if (!CLIENT_ID)
		throw new Error("Google sign-in is not configured (REACT_APP_GOOGLE_CLIENT_ID).");
	if (!_pending) {
		_pending = signIn(interactive, deps).finally(() => {
			_pending = null;
		});
	}
	return _pending;
}

/**
 * Forget the token for this tab and revoke it at Google.
 * @param {AuthDeps} [deps]
 */
export function signOutGoogle({ now = Date.now } = {}) {
	const t = _token || readStored(now);
	store(null);
	if (t?.value && gisOauth()) gisOauth().revoke(t.value, () => {});
}
